// CB:BEGIN admin-projects-list
import React, { useState, useEffect } from 'react';
import { FaSearch, FaTrash } from 'react-icons/fa';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@clerk/clerk-react';
// CB:BEGIN health-badge-import
import { ProjectHealthBadge, computeProjectHealth } from '../../components/admin/ProjectHealthBadge';
// CB:END health-badge-import
import { getStatusPill, adminBtn, adminCard } from '../../styles/adminTokens';

interface Project {
  id: string;
  address: string;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  // CB:BEGIN pm-channel-admin-fields
  requestorType?: string; // 'HOMEOWNER' | 'PROPERTY_MANAGER'
  priority?: string;
  // CB:END pm-channel-admin-fields
  roofReportUrl?: string; // Legacy field
  roofReportOriginalKey?: string;
  roofReportRedactedKey?: string;
  roofReportUploadedAt?: string;
  roofReportPagesOriginal?: number;
  roofReportPagesRedacted?: number;
  homeowner?: {
    firstName?: string;
    lastName?: string;
    user: {
      email: string;
      phone?: string;
    };
  };
  bids: Array<{
    id: string;
    amount: number;
    status: string;
  }>;
  manualBids: Array<{
    id: string;
    contractorName: string;
    priceCents: number;
    isWinner: boolean;
  }>;
  phases: Array<{
    id: string;
    number: number;
    status: string;
  }>;
  _count: {
    bids: number;
    manualBids: number;
    phases: number;
  };
}

interface ProjectsResponse {
  projects: Project[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface AdminProjectsListProps {
  onNavigate?: (screen: string) => void;
}

interface InvoiceMetrics {
  period: string;
  totalIssued: number;
  totalFunded: number;
  totalFinanced: number;
  totalCompleted: number;
  totalDisputed: number;
  stuckInPendingFunding: number;
  fundingConversionRate: string;
  financedRate: string;
  disputeRate: string;
}

const AdminProjectsList: React.FC<AdminProjectsListProps> = ({ onNavigate }) => {
  // Clerk user is not currently used on this page
  const api = useApi();
  const { getToken } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [invoiceMetrics, setInvoiceMetrics] = useState<InvoiceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('');
  // CB:BEGIN pm-channel-admin-filter
  const [requestorTypeFilter, setRequestorTypeFilter] = useState('');
  // CB:END pm-channel-admin-filter
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [showRoofReportModal, setShowRoofReportModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // CB:BEGIN send-invite-modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteTtlHours, setInviteTtlHours] = useState('72');
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  // CB:END send-invite-modal

  // CB:BEGIN admin-projects-format-address
  const formatProjectAddress = (project: Project): string => {
    const base = (project.address || '').trim();
    const city = (project.city || '').trim();
    const state = (project.state || '').trim();
    const zip = (project.zip || '').trim();

    const locality = [city, state].filter(Boolean).join(', ');
    const suffix = [locality, zip].filter(Boolean).join(' ');
    if (!suffix) return base || '—';
    if (base.toLowerCase().includes(locality.toLowerCase()) || base.toLowerCase().includes(zip.toLowerCase())) {
      return base;
    }
    return `${base}, ${suffix}`;
  };
  // CB:END admin-projects-format-address

  // CB:BEGIN admin-projects-status-map
  const fetchProjects = async (page = 1, search = '', status = '', projectType = '', requestorType = '') => {
    try {
      setLoading(true);
      // Map UI-friendly statuses to backend enums
      const statusMap: Record<string, string> = {
        'DRAFT': 'DRAFT',
        'PROCESSING': 'REPORT_REQUESTED',
        'BIDDING': 'BIDS_COLLECTING',
        'BIDS_READY': 'BIDS_READY',
        'DECIDING': 'DECIDING',
        'ACTIVE': 'ACTIVE_P1',
        'COMPLETED': 'COMPLETED'
      };
      const statusParam = statusMap[status] || status;
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
        ...(statusParam && { status: statusParam }),
        ...(projectType && { projectType }),
        // CB:BEGIN pm-channel-admin-filter
        ...(requestorType && { requestorType }),
        // CB:END pm-channel-admin-filter
      });
      const data = await api.get<ProjectsResponse>(`/api/admin/projects?${params}`);
      
      // CB:BEGIN defensive-projects-response
      // Handle 403 errors and malformed responses
      if (data && 'error' in data) {
        const errorMessage = typeof data.error === 'string' ? data.error : 'Forbidden';
        throw new Error(errorMessage);
      }
      if (!data || !Array.isArray(data.projects)) {
        console.warn('⚠️ Invalid projects response:', data);
        setProjects([]);
        setError('Invalid response format');
        return;
      }
      // CB:END defensive-projects-response
      
      setProjects(data.projects || []);
      setTotalPages(data.pagination?.pages || 1);
      setCurrentPage(data.pagination?.page || 1);
    } catch (err: any) {
      // CB:BEGIN quiet-api-unreachable
      const isNetworkError =
        err?.message === 'API_UNREACHABLE' ||
        (err instanceof TypeError && err?.message?.includes?.('fetch'));
      if (!isNetworkError) console.error('❌ Error fetching projects:', err);
      // CB:END quiet-api-unreachable
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
      setProjects([]); // Ensure empty array on error
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
  // CB:END admin-projects-status-map

  useEffect(() => {
    fetchProjects(currentPage, searchTerm, statusFilter, projectTypeFilter, requestorTypeFilter);
  }, [currentPage, searchTerm, statusFilter, projectTypeFilter, requestorTypeFilter]);

  // CB:BEGIN invoice-metrics-fetch
  useEffect(() => {
    api.get<InvoiceMetrics>('/api/invoices/admin/metrics').then((data) => {
      if (data && 'totalIssued' in data) setInvoiceMetrics(data);
    }).catch(() => {});
  }, []);
  // CB:END invoice-metrics-fetch

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProjects(1, searchTerm, statusFilter, projectTypeFilter, requestorTypeFilter);
  };

  // const getStatusColor = (status: string) => 'bg-gray-100 text-gray-800';

  // const formatCurrency = (cents: number) => '$0.00';

  if (loading && projects.length === 0) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 0' }}>
        {[0,1,2,3,4].map(i => (
          <div key={i} style={{ height: 52, background: '#f3f4f6', borderRadius: 6, marginBottom: 8, animation: 'pulse 1.5s infinite' }} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Projects</h1>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: '4px 0 0 0' }}>All platform projects</p>
        </div>
      </div>

      {/* CB:BEGIN invoice-metrics-bar */}
      {invoiceMetrics && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Issued (30d)', value: invoiceMetrics.totalIssued },
            { label: 'Funded', value: `${invoiceMetrics.totalFunded} (${invoiceMetrics.fundingConversionRate})` },
            { label: 'Financed', value: `${invoiceMetrics.totalFinanced} (${invoiceMetrics.financedRate})` },
            { label: 'Completed', value: invoiceMetrics.totalCompleted },
            { label: 'Stuck (>48h)', value: invoiceMetrics.stuckInPendingFunding, highlight: invoiceMetrics.stuckInPendingFunding > 0 },
            { label: 'Disputed', value: `${invoiceMetrics.totalDisputed} (${invoiceMetrics.disputeRate})`, highlight: invoiceMetrics.totalDisputed > 0 },
          ].map((m) => (
            <div key={m.label} style={{
              padding: '8px 14px',
              background: m.highlight ? '#fef2f2' : '#f9fafb',
              border: `1px solid ${m.highlight ? '#fca5a5' : '#e5e7eb'}`,
              borderRadius: 8,
              fontSize: 12,
            }}>
              <div style={{ color: '#6b7280', marginBottom: 2 }}>{m.label}</div>
              <div style={{ fontWeight: 700, color: m.highlight ? '#dc2626' : '#111827' }}>{String(m.value)}</div>
            </div>
          ))}
        </div>
      )}
      {/* CB:END invoice-metrics-bar */}

      {/* ── Search & filter bar ── */}
      <div style={{ ...adminCard, marginBottom: 16, padding: '12px 16px' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <FaSearch style={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13, pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Address, homeowner name or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%', padding: '7px 10px 7px 30px',
                border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minWidth: 140 }}
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PROCESSING">Processing</option>
            <option value="BIDDING">Bidding</option>
            <option value="BIDS_READY">Bids Ready</option>
            <option value="DECIDING">Deciding</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select
            value={projectTypeFilter}
            onChange={(e) => setProjectTypeFilter(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minWidth: 120 }}
          >
            <option value="">All Types</option>
            <option value="ROOF">Roof</option>
            <option value="GUTTERS">Gutters</option>
            <option value="SIDING">Siding</option>
            <option value="WINDOWS">Windows</option>
          </select>
          {/* CB:BEGIN pm-channel-admin-filter */}
          <select
            value={requestorTypeFilter}
            onChange={(e) => setRequestorTypeFilter(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minWidth: 160 }}
          >
            <option value="">All Origins</option>
            <option value="HOMEOWNER">Homeowner</option>
            <option value="PROPERTY_MANAGER">Property Manager</option>
          </select>
          {/* CB:END pm-channel-admin-filter */}
          <button type="submit" style={adminBtn.primary}>Search</button>
        </form>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          {error}
        </div>
      )}

      {/* ── Projects table ── */}
      <div style={{ ...adminCard, padding: 0, overflow: 'hidden' }}>
        {projects.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Project</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Homeowner</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Health</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Bids</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Created</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project, idx) => {
                const pill = getStatusPill(project.status);
                const isLast = idx === projects.length - 1;
                const hasReport = !!(project.roofReportOriginalKey || project.roofReportUrl);

                return (
                  <tr
                    key={project.id}
                    style={{ borderBottom: isLast ? 'none' : '1px solid #f3f4f6', background: '#fff' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#f9fafb'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#fff'; }}
                  >
                    {/* Address + report chips */}
                    <td style={{ padding: '12px 16px', maxWidth: 280 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formatProjectAddress(project)}
                      </div>
                      {/* CB:BEGIN pm-channel-admin-origin-badge */}
                      {project.requestorType === 'PROPERTY_MANAGER' && (
                        <span style={{ display: 'inline-block', background: '#ede9fe', color: '#6d28d9', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 4, marginTop: 3, letterSpacing: '0.02em' }}>
                          PM
                        </span>
                      )}
                      {/* CB:END pm-channel-admin-origin-badge */}
                      <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                        {!hasReport && (
                          <button
                            onClick={() => { setSelectedProject(project); setShowRoofReportModal(true); setUploading(false); setUploadProgress(0); }}
                            style={{ fontSize: 10, padding: '2px 6px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                          >
                            + Add report
                          </button>
                        )}
                        {/* CB:BEGIN view-report-buttons */}
                        {hasReport && (
                          <>
                            <button
                              onClick={async () => {
                                try {
                                  const token = await getToken();
                                  const { apiUrl } = await import('@/lib/getApiUrl');
                                  const res = await fetch(apiUrl(`/api/admin/projects/${project.id}/roof-report/stream?type=original`), {
                                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                                  });
                                  if (!res.ok) { alert('Failed to load report'); return; }
                                  const blob = await res.blob();
                                  const objectUrl = URL.createObjectURL(blob);
                                  window.open(objectUrl, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                                } catch { alert('Failed to open original report'); }
                              }}
                              style={{ fontSize: 10, padding: '2px 6px', background: '#ede9fe', color: '#5b21b6', border: '1px solid #ddd6fe', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                            >
                              Original
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const token = await getToken();
                                  const { apiUrl } = await import('@/lib/getApiUrl');
                                  const res = await fetch(apiUrl(`/api/admin/projects/${project.id}/roof-report/stream?type=redacted`), {
                                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                                  });
                                  if (!res.ok) { alert('Redacted report not available'); return; }
                                  const blob = await res.blob();
                                  const objectUrl = URL.createObjectURL(blob);
                                  window.open(objectUrl, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                                } catch { alert('Failed to open redacted report'); }
                              }}
                              style={{ fontSize: 10, padding: '2px 6px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                            >
                              Redacted
                            </button>
                            {/* CB:BEGIN clear-roof-report-button */}
                            <button
                              onClick={async () => {
                                if (!window.confirm('Clear this roof report? The project will be reset to REQUESTED status so you can re-upload.')) return;
                                try {
                                  await api.delete(`/api/admin/projects/${project.id}/roof-report`);
                                  setProjects(prev => prev.map(p =>
                                    p.id === project.id
                                      ? { ...p, roofReportUrl: undefined, roofReportOriginalKey: undefined, roofReportRedactedKey: undefined, status: 'REQUESTED' }
                                      : p
                                  ));
                                } catch (err: any) {
                                  alert(err?.response?.data?.error || 'Failed to clear roof report');
                                }
                              }}
                              style={{ fontSize: 10, padding: '2px 6px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                            >
                              Clear
                            </button>
                            {/* CB:END clear-roof-report-button */}
                          </>
                        )}
                        {/* CB:END view-report-buttons */}
                      </div>
                    </td>

                    {/* Homeowner */}
                    <td style={{ padding: '12px 12px', maxWidth: 180 }}>
                      <div style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {project.homeowner
                          ? (`${project.homeowner.firstName || ''} ${project.homeowner.lastName || ''}`.trim() || 'Unnamed')
                          : '—'}
                      </div>
                      {project.homeowner?.user.email && (
                        <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {project.homeowner.user.email}
                        </div>
                      )}
                    </td>

                    {/* Status pill */}
                    <td style={{ padding: '12px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 8px', borderRadius: 20,
                        background: pill.bg, color: pill.text,
                        fontSize: 11, fontWeight: 600,
                      }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: pill.dot, display: 'inline-block', flexShrink: 0 }} />
                        {pill.label}
                      </span>
                    </td>

                    {/* Health badge */}
                    {/* CB:BEGIN health-badge-in-list */}
                    <td style={{ padding: '12px 12px' }}>
                      <ProjectHealthBadge
                        health={computeProjectHealth(project.status, project.updatedAt, (project as any).pendingAdminAction).health}
                        size="sm"
                      />
                    </td>
                    {/* CB:END health-badge-in-list */}

                    {/* Bid count */}
                    <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                        {project._count.bids + (project._count.manualBids || 0)}
                      </span>
                    </td>

                    {/* Created date */}
                    <td style={{ padding: '12px 12px', whiteSpace: 'nowrap', fontSize: 12, color: '#6b7280' }}>
                      {new Date(project.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          onClick={() => onNavigate?.(`admin-project-edit-${project.id}`)}
                          style={{ ...adminBtn.secondary, padding: '5px 12px', fontSize: 12 }}
                        >
                          Open
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6b7280' }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.25 }}>🏗️</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 4 }}>No projects found</div>
            <div style={{ fontSize: 13 }}>
              {searchTerm || statusFilter || projectTypeFilter ? 'Try adjusting your filters.' : 'No projects have been created yet.'}
            </div>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{ ...adminBtn.secondary, opacity: currentPage === 1 ? 0.45 : 1 }}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{ ...adminBtn.secondary, opacity: currentPage === totalPages ? 0.45 : 1 }}
            >
              Next
            </button>
          </div>
        </div>
      )}

        {/* CB:BEGIN roof-report-modal */}
        {showRoofReportModal && selectedProject && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => {
              setShowRoofReportModal(false);
              setSelectedProject(null);
              setUploading(false);
              setUploadProgress(0);
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '32px',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600' }}>
                Upload Roof Report
              </h2>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                  Project: <strong>{selectedProject.address}</strong>
                </p>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                  Upload a PDF file that contractors can view when bidding on this project.
                </p>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  PDF File
                </label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  id="roof-report-upload"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !selectedProject) return;
                    
                    // Validate file type
                    if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
                      alert('Please upload a PDF file');
                      return;
                    }
                    
                    // Validate file size (max 10MB)
                    if (file.size > 10 * 1024 * 1024) {
                      alert('File size must be less than 10MB');
                      return;
                    }
                    
                    setUploading(true);
                    setUploadProgress(0);
                    
                    try {
                      // Upload file through backend (avoids CORS issues with direct S3 upload)
                      setUploadProgress(30);
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      setUploadProgress(50);
                      
                      // Don't set Content-Type header - let browser set it automatically for FormData
                      const uploadResponse = await api.post(`/api/admin/projects/${selectedProject.id}/roof-report/upload`, formData);
                      
                      setUploadProgress(100);
                      
                      // CB:BEGIN fix-upload-response-handling
                      // useApi.post returns JSON directly (not axios response)
                      // Success - show metadata if available
                      const metadata = (uploadResponse as any)?.metadata;
                      if (metadata) {
                      // CB:END fix-upload-response-handling
                        const message = `Roof report uploaded and redacted successfully!\n\nOriginal: ${metadata.originalPages} pages\nRedacted: ${metadata.redactedPages} pages\nPages removed: ${metadata.pagesDeleted || 0}`;
                        alert(message);
                      } else {
                        alert('Roof report uploaded successfully!');
                      }
                      
                      setShowRoofReportModal(false);
                      setSelectedProject(null);
                      setUploading(false);
                      setUploadProgress(0);
                      
                      // Refresh projects list to show updated report status
                      await fetchProjects(currentPage, searchTerm, statusFilter, projectTypeFilter);
                      
                      // Reset file input
                      const fileInput = document.getElementById('roof-report-upload') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    } catch (error: any) {
                      console.error('Upload error:', error);
                      const errorMessage = error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Failed to upload roof report';
                      alert(`Upload failed: ${errorMessage}`);
                    } finally {
                      setUploading(false);
                      setUploadProgress(0);
                      // Reset file input
                      const fileInput = document.getElementById('roof-report-upload') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    }
                  }}
                  disabled={uploading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.6 : 1
                  }}
                />
                {uploading && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ 
                      width: '100%', 
                      height: '8px', 
                      background: '#e5e7eb', 
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${uploadProgress}%`,
                        height: '100%',
                        background: '#10b981',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', textAlign: 'center' }}>
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Contractors will be able to view this report when bidding on the project. Maximum file size: 10MB.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowRoofReportModal(false);
                    setSelectedProject(null);
                    setUploading(false);
                    setUploadProgress(0);
                  }}
                  disabled={uploading}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    background: 'white',
                    fontWeight: '600',
                    opacity: uploading ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* CB:END roof-report-modal */}

        {/* CB:BEGIN send-invite-modal */}
        {showInviteModal && selectedProject && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => {
              setShowInviteModal(false);
              setSelectedProject(null);
              setInviteEmail('');
              setInviteUrl('');
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '32px',
                width: '90%',
                maxWidth: '500px'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: '600' }}>
                Send Project to Contractor
              </h2>
              <div style={{ marginBottom: '16px' }}>
                <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                  Project: <strong>{selectedProject.address}</strong>
                </p>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                  Create an invite link for a contractor to bid on this project. They'll receive an email with the link.
                </p>
              </div>
              {!inviteUrl ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                      Contractor Email *
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="contractor@example.com"
                      disabled={inviteLoading}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                      Invite Expires In (hours)
                    </label>
                    <input
                      type="number"
                      value={inviteTtlHours}
                      onChange={(e) => setInviteTtlHours(e.target.value)}
                      min="1"
                      max="168"
                      disabled={inviteLoading}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Default: 72 hours (3 days)
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        setSelectedProject(null);
                        setInviteEmail('');
                        setInviteUrl('');
                      }}
                      disabled={inviteLoading}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: inviteLoading ? 'not-allowed' : 'pointer',
                        background: 'white',
                        fontWeight: '600',
                        opacity: inviteLoading ? 0.6 : 1
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!inviteEmail || !inviteEmail.includes('@')) {
                          alert('Please enter a valid email address');
                          return;
                        }
                        setInviteLoading(true);
                        try {
                          const response = await api.post(`/api/admin/projects/${selectedProject.id}/invites`, {
                            email: inviteEmail,
                            ttlHours: inviteTtlHours ? parseInt(inviteTtlHours, 10) : undefined
                          });
                          setInviteUrl(response.inviteUrl);
                        } catch (error: any) {
                          alert(error?.response?.data?.error || error?.message || 'Failed to create invite');
                        } finally {
                          setInviteLoading(false);
                        }
                      }}
                      disabled={inviteLoading || !inviteEmail}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (inviteLoading || !inviteEmail) ? 'not-allowed' : 'pointer',
                        background: '#7c3aed',
                        color: 'white',
                        fontWeight: '600',
                        opacity: (inviteLoading || !inviteEmail) ? 0.6 : 1
                      }}
                    >
                      {inviteLoading ? 'Creating...' : 'Create Invite'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '24px', padding: '16px', background: '#f3f4f6', borderRadius: '6px' }}>
                    <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                      Invite Link Created
                    </p>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                      Copy this link and share it with the contractor, or they'll receive it via email.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={inviteUrl}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '12px',
                          background: 'white'
                        }}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(inviteUrl);
                          alert('Link copied to clipboard!');
                        }}
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '6px',
                          background: '#7c3aed',
                          color: 'white',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        setSelectedProject(null);
                        setInviteEmail('');
                        setInviteUrl('');
                      }}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        background: 'white',
                        fontWeight: '600'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {/* CB:END send-invite-modal */}
    </div>
  );
};

export default AdminProjectsList;
// CB:END admin-projects-list

